import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { accessToken, refreshUser } = useAuth();
  
  const [checking, setChecking] = useState(true);
  const [orderInfo, setOrderInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No session ID provided');
      setChecking(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${API}/payment/session/${sessionId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        
        const data = response.data;
        
        if (data.payment_status === 'paid') {
          setOrderInfo(data);
          setChecking(false);
          // Refresh user data
          await refreshUser();
          return;
        } else if (data.status === 'expired' || data.status === 'failed') {
          setError('Payment was not completed. Please try again.');
          setChecking(false);
          return;
        }
        
        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          // After max attempts, assume it's still processing
          setOrderInfo(data);
          setChecking(false);
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setError('Unable to verify payment status. Please check your dashboard.');
          setChecking(false);
        }
      }
    };

    poll();
  };

  if (checking) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <Loader2 className="h-16 w-16 text-[#00B4B4] animate-spin mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-[#1A365D]">Confirming Payment...</h1>
          <p className="text-slate-600 mt-2">Please wait while we verify your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#1A365D]">Payment Issue</h1>
          <p className="text-slate-600 mt-2">{error}</p>
          <div className="mt-6 flex justify-center gap-4">
            <Link to="/pricing">
              <Button className="bg-[#00B4B4] text-white">Try Again</Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline">Check Dashboard</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-[80vh] flex items-center justify-center py-12 px-4"
      data-testid="payment-success-page"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg text-center"
      >
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 10 }}
            className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="h-10 w-10 text-emerald-600" />
          </motion.div>
          
          <h1 className="text-2xl font-semibold text-[#1A365D]">
            Payment Successful!
          </h1>
          
          <p className="text-slate-600 mt-4">
            Thank you for your purchase. Your IRS Peace-of-Mind Review is now active.
          </p>
          
          {orderInfo && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg text-left">
              <h3 className="font-medium text-[#1A365D] mb-2">Order Details</h3>
              <div className="space-y-1 text-sm">
                {orderInfo.order_number && (
                  <p className="text-slate-600">
                    Order: <span className="font-medium font-mono">{orderInfo.order_number}</span>
                  </p>
                )}
                {orderInfo.amount_total && (
                  <p className="text-slate-600">
                    Amount: <span className="font-medium">${(orderInfo.amount_total / 100).toFixed(2)}</span>
                  </p>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-8 space-y-4">
            <h3 className="font-medium text-[#1A365D]">What's Next?</h3>
            <ul className="text-sm text-slate-600 space-y-2 text-left">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Our team will begin reviewing your IRS transcripts</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>You'll receive updates via email</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Expect your comprehensive report within 5-7 business days</span>
              </li>
            </ul>
          </div>
          
          <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }} className="mt-8">
            <Link to="/dashboard">
              <Button className="w-full bg-[linear-gradient(160deg,_#00B4B4,_#2BC3C3)] text-white py-3">
                Go to Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
